import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import { brand5 } from "@/brand/content";
import { FaithMark } from "@/components/brand/FaithMark";

/**
 * SiteHeader5 — Concept 5 header, warm-dominant variant.
 *
 * Nav wired to interior pages where they exist (About, Services,
 * Insights, Speaking) and to homepage anchors otherwise (Process,
 * Contact). All destinations are fully qualified so links work from
 * both the homepage and interior pages.
 */
const nav = [
  { label: "Services", href: "/concept-e#pathways" },
  { label: "About", href: "/concept-e/about" },
  { label: "Process", href: "/concept-e#process" },
  { label: "Insights", href: "/concept-e/insights" },
  { label: "Speaking", href: "/concept-e/speaking" },
  { label: "Contact", href: "/concept-e#contact" },
];

export function SiteHeader5({
  homeHref = "/concept-e",
  ctaHref = "/concept-e#contact",
}) {
  return (
    <header
      className="sticky top-0 z-40 backdrop-blur"
      style={{
        background: `${C.surfaceLight}F0`,
        borderBottom: `1px solid ${C.brassSoft}`,
      }}
    >
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 h-24 flex items-center justify-between">
        <Link
          to={homeHref}
          data-testid="brand-mark"
          className="inline-flex"
          aria-label="Faith 1st — home"
        >
          <FaithMark size="sm" tone="ink" subtitle={brand5.markSubtitle} />
        </Link>

        <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
          {nav.map((l) => {
            const isRoute = !l.href.includes("#");
            const isAnchor = l.href.includes("#");
            const className =
              "font-inter text-[12px] uppercase font-medium transition-colors whitespace-nowrap";
            const style = {
              color: C.inkOnLight,
              letterSpacing: "0.20em",
            };
            const handleEnter = (e) =>
              (e.currentTarget.style.color = C.accentMetal);
            const handleLeave = (e) =>
              (e.currentTarget.style.color = C.inkOnLight);
            const testId = `nav-link-${l.label
              .toLowerCase()
              .replace(/\s+/g, "-")}`;

            return isRoute && !isAnchor ? (
              <Link
                key={l.label}
                to={l.href}
                data-testid={testId}
                className={className}
                style={style}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.href}
                data-testid={testId}
                className={className}
                style={style}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
              >
                {l.label}
              </a>
            );
          })}
        </nav>

        <a
          href={ctaHref}
          data-testid="nav-cta"
          className="hidden xl:inline-flex items-center gap-2.5 px-5 py-3 font-inter text-[11px] uppercase font-semibold whitespace-nowrap transition-colors"
          style={{
            background: C.surfacePrimary,
            color: C.textOnDark,
            letterSpacing: "0.22em",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = C.accentMetal;
            e.currentTarget.style.color = C.bgDark;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = C.surfacePrimary;
            e.currentTarget.style.color = C.textOnDark;
          }}
        >
          Book a consultation
          <ArrowRight size={12} weight="bold" />
        </a>
      </div>
    </header>
  );
}
